import { useEffect, useState } from "react";

interface FieldConnectingLineProps {
  /** DOM-attribute selector identifying the active field row in the side panel. */
  sourceSelector: string;
  /** Selector for the rendered bbox SVG rect on the document. */
  targetSelector?: string;
  /** Re-measure when this key changes (e.g. activeBoundary string). */
  changeKey?: string | null;
}

interface Point {
  x: number;
  y: number;
}

/**
 * Lavender connecting curve from the active field row in the right rail
 * to the highlighted bbox on the document image.
 *
 * Coordinates are computed from the rendered DOM elements via
 * `getBoundingClientRect` (no natural-pixel math), so the endpoint always
 * lands precisely on the bbox edge regardless of zoom or image scale.
 *
 * Recomputes on:
 *   - changeKey change (active field/bbox)
 *   - Window resize
 *   - Any ancestor scroll (capturing-phase listener)
 *   - Two RAFs after mount so the popover doesn't shift layout post-paint
 */
export function FieldConnectingLine({
  sourceSelector,
  targetSelector = "[data-active-bbox='true']",
  changeKey,
}: FieldConnectingLineProps) {
  const [source, setSource] = useState<Point | null>(null);
  const [target, setTarget] = useState<Point | null>(null);

  useEffect(() => {
    if (!sourceSelector) {
      setSource(null);
      setTarget(null);
      return;
    }

    const compute = () => {
      const sourceEl = document.querySelector(sourceSelector);
      const targetEl = document.querySelector(targetSelector);
      if (!sourceEl || !targetEl) {
        setSource(null);
        setTarget(null);
        return;
      }

      const srcRect = sourceEl.getBoundingClientRect();
      const tgtRect = targetEl.getBoundingClientRect();

      // Source: anchor at the LEFT edge of the row, vertical center.
      // (Side panel sits to the right of the document.)
      const sourceX = srcRect.left;
      const sourceY = srcRect.top + srcRect.height / 2;

      // Target: the bbox edge nearest the source, at the bbox's vertical center.
      const targetCenterY = tgtRect.top + tgtRect.height / 2;
      const targetEdgeX =
        sourceX > tgtRect.right
          ? tgtRect.right // source is to the right → arrow into bbox's right edge
          : sourceX < tgtRect.left
          ? tgtRect.left // source is to the left → arrow into bbox's left edge
          : tgtRect.right; // overlapping x → fall back to right edge

      setSource({ x: sourceX, y: sourceY });
      setTarget({ x: targetEdgeX, y: targetCenterY });
    };

    // First measurement: defer two RAFs so the popover/layout has settled.
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(compute);
    });

    const onChange = () => compute();
    window.addEventListener("resize", onChange);
    window.addEventListener("scroll", onChange, true);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.removeEventListener("resize", onChange);
      window.removeEventListener("scroll", onChange, true);
    };
  }, [sourceSelector, targetSelector, changeKey]);

  if (!source || !target) return null;

  // Smooth bezier — pull horizontally so the curve eases in/out.
  const dx = target.x - source.x;
  const c1x = source.x + dx * 0.4;
  const c1y = source.y;
  const c2x = source.x + dx * 0.6;
  const c2y = target.y;
  const path = `M ${source.x} ${source.y} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${target.x} ${target.y}`;

  // Tangent direction at the target so the arrow head points correctly.
  const tangent = Math.atan2(target.y - c2y, target.x - c2x);
  const arrowDeg = (tangent * 180) / Math.PI;

  return (
    <svg
      className="pointer-events-none fixed inset-0 z-20"
      style={{ width: "100vw", height: "100vh" }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="connlineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(99, 102, 241, 0.45)" />
          <stop offset="100%" stopColor="rgba(99, 102, 241, 0.95)" />
        </linearGradient>
      </defs>

      {/* Soft glow underlay */}
      <path d={path} fill="none" stroke="rgba(99, 102, 241, 0.10)" strokeWidth={6} />

      {/* Main line */}
      <path
        d={path}
        fill="none"
        stroke="url(#connlineGrad)"
        strokeWidth={1.5}
        strokeLinecap="round"
      />

      {/* Source dot */}
      <circle cx={source.x} cy={source.y} r={3} fill="rgba(99, 102, 241, 0.9)" />

      {/* Arrow head meeting the bbox edge */}
      <g transform={`translate(${target.x} ${target.y}) rotate(${arrowDeg})`}>
        <path d="M 0 0 L -7 -4 L -5 0 L -7 4 Z" fill="rgba(99, 102, 241, 1)" />
      </g>
    </svg>
  );
}
